import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ValidateIcon,
  Refresh as RefreshIcon,
  MoneyOff as AcompteIcon,
  PictureAsPdf as PdfIcon,
  FilterListOff as FilterListOffIcon,
  AccountBalance as VirementIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { acomptesAPI, employeesAPI, auditLogsAPI } from '../../../services/api';
import {
  Acompte,
  AcompteStats,
  STATUT_ACOMPTE_OPTIONS,
  getStatutAcompteLabel,
  getStatutAcompteColor,
} from '../../../types/acompte';
import { EmployeeDropdownItem } from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import { getModuleTableTheme } from '../../../theme/tableTheme';
// generateAcomptePDF is dynamically imported in handleGeneratePDF so the
// jspdf chunk only loads when the user requests an acompte PDF.
import AcompteFormDialog from './AcompteFormDialog';
import AcompteValidationDialog from './AcompteValidationDialog';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';

const HR_COLOR = '#00897B';

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const ACOMPTE_FILTER_SCHEMA = {
  ref_acompte:   { type: 'text',         param: 'ref_acompte' },
  date_demande:  { type: 'dateRange',    paramFrom: 'date_demande_from',  paramTo: 'date_demande_to' },
  employee_id:   { type: 'autocomplete', param: 'employee_id', transform: (v: string) => Number(v) },
  montant:       { type: 'numberRange',  paramMin: 'montant_min',         paramMax: 'montant_max' },
  date_paiement: { type: 'dateRange',    paramFrom: 'date_paiement_from', paramTo: 'date_paiement_to' },
  statut:        { type: 'select',       param: 'statut' },
} as const;

const AcomptesManagement: React.FC = () => {
  const [acomptes, setAcomptes] = useState<Acompte[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState<AcompteStats | null>(null);

  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [selectedAcompte, setSelectedAcompte] = useState<Acompte | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [acompteToDelete, setAcompteToDelete] = useState<Acompte | null>(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [acompteToValidate, setAcompteToValidate] = useState<Acompte | null>(null);

  // Employees for dropdown
  const [employees, setEmployees] = useState<EmployeeDropdownItem[]>([]);

  // État de Virement month/year navigator (deduction month — mois/annee_prelevement)
  const [virementMonth, setVirementMonth] = useState(new Date().getMonth() + 1);
  const [virementYear, setVirementYear] = useState(new Date().getFullYear());

  const { showSuccess, showError } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    ACOMPTE_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  const fetchAcomptes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await acomptesAPI.getAll({
        page: page + 1,
        size: rowsPerPage,
        search: search || undefined,
        mois_prelevement: virementMonth,
        annee_prelevement: virementYear,
        ...toQueryParams(),
      });
      setAcomptes(response.items);
      setTotalCount(response.total);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, filters, virementMonth, virementYear, showError]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await acomptesAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const data = await employeesAPI.getAllForDropdown();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  useEffect(() => {
    fetchAcomptes();
    fetchStats();
  }, [fetchAcomptes, fetchStats]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddAcompte = () => {
    setSelectedAcompte(null);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleEditAcompte = (acompte: Acompte) => {
    setSelectedAcompte(acompte);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (acompte: Acompte) => {
    setAcompteToDelete(acompte);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!acompteToDelete) return;
    try {
      await acomptesAPI.delete(acompteToDelete.id);
      showSuccess('Demande d\'acompte supprimee avec succes');
      fetchAcomptes();
      fetchStats();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setDeleteDialogOpen(false);
      setAcompteToDelete(null);
    }
  };

  const handleValidateClick = (acompte: Acompte) => {
    setAcompteToValidate(acompte);
    setValidationDialogOpen(true);
  };

  const handleSuccess = () => {
    fetchAcomptes();
    fetchStats();
  };

  const handleGeneratePDF = (acompte: Acompte) => {
    openPreview({
      getBlob: async () => {
        const { generateAcomptePDF } = await import('../../../utils/pdfGenerator');
        return generateAcomptePDF({
          id: acompte.id,
          ref_acompte: acompte.ref_acompte,
          montant: acompte.montant,
          date_demande: acompte.date_demande,
          date_paiement: acompte.date_paiement,
          motif: acompte.motif,
          employee: acompte.employee ? {
            nom_complet: acompte.employee.nom_complet,
            matricule: acompte.employee.matricule,
            fonction: acompte.employee.fonction,
          } : null,
        }, true) as Blob;
      },
      fileName: `Acompte_${acompte.ref_acompte || acompte.id}.pdf`,
    });
    auditLogsAPI.logPdfDownload('Acompte', acompte.id, `Aperçu PDF - ${acompte.ref_acompte}`);
  };

  const handlePreviousVirementMonth = () => {
    setPage(0);
    if (virementMonth === 1) {
      setVirementMonth(12);
      setVirementYear(virementYear - 1);
    } else {
      setVirementMonth(virementMonth - 1);
    }
  };

  const handleNextVirementMonth = () => {
    setPage(0);
    if (virementMonth === 12) {
      setVirementMonth(1);
      setVirementYear(virementYear + 1);
    } else {
      setVirementMonth(virementMonth + 1);
    }
  };

  // État de Virement des Acomptes: Excel downloads, PDF opens in the preview viewer.
  const handleEtatVirement = async () => {
    try {
      const { items } = await acomptesAPI.getEtatVirement(virementMonth, virementYear);
      if (!items || items.length === 0) {
        showError(`Aucun acompte à prélever pour ${MONTH_NAMES[virementMonth - 1]} ${virementYear}`);
        return;
      }
      const { generateEtatVirementAcompteExcel } = await import('../../../utils/etatVirementAcompteExcel');
      await generateEtatVirementAcompteExcel(items, virementMonth, virementYear);
      openPreview({
        getBlob: async () => {
          const { generateEtatVirementAcomptePDF } = await import('../../../utils/etatVirementAcomptePdf');
          return generateEtatVirementAcomptePDF(items, virementMonth, virementYear, true) as Blob;
        },
        fileName: `Etat_Virement_Acomptes_${MONTH_NAMES[virementMonth - 1]}_${virementYear}.pdf`,
      });
      auditLogsAPI.logPdfDownload('EtatVirementAcompte', 0, `État de virement acomptes - ${MONTH_NAMES[virementMonth - 1]} ${virementYear}`);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) + ' DH';
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: HR_COLOR, color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AcompteIcon />
                <Typography variant="h6">{stats?.total_demandes || 0}</Typography>
              </Box>
              <Typography variant="body2">Total Demandes</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#FFA726', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats?.total_en_attente || 0}</Typography>
              <Typography variant="body2">En Attente</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#42A5F5', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats?.total_validees || 0}</Typography>
              <Typography variant="body2">Validees</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#66BB6A', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats?.total_payees || 0}</Typography>
              <Typography variant="body2">Payees</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Financial Summary */}
      {stats && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Montant Total Valide</Typography>
              <Typography variant="h6" sx={{ color: '#42A5F5' }}>
                {formatMoney(stats.total_montant_valide)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Montant Total Paye</Typography>
              <Typography variant="h6" sx={{ color: '#66BB6A' }}>
                {formatMoney(stats.total_montant_paye)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Rechercher un employe ou ref..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          {hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              Effacer les filtres
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* État de Virement — month navigator (prélèvement month) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <IconButton size="small" onClick={handlePreviousVirementMonth} sx={{ borderRadius: 0 }}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              variant="body2"
              sx={{ minWidth: 130, textAlign: 'center', fontWeight: 500, userSelect: 'none' }}
            >
              {MONTH_NAMES[virementMonth - 1]} {virementYear}
            </Typography>
            <IconButton size="small" onClick={handleNextVirementMonth} sx={{ borderRadius: 0 }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            startIcon={<VirementIcon />}
            onClick={handleEtatVirement}
            sx={{ color: HR_COLOR, borderColor: HR_COLOR, whiteSpace: 'nowrap', '&:hover': { borderColor: '#00695C', bgcolor: 'rgba(0,137,123,0.04)' } }}
          >
            État de Virement
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddAcompte}
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
          >
            Nouvelle Demande
          </Button>

          <Tooltip title="Actualiser">
            <IconButton onClick={() => { fetchAcomptes(); fetchStats(); }} sx={{ color: HR_COLOR }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Table */}
        <TableContainer sx={getModuleTableTheme('hr')}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Ref.
                    <ColumnFilter type="text" value={filters.ref_acompte} onChange={(v) => update('ref_acompte', v)} label="référence" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date Demande
                    <ColumnFilter type="dateRange" value={filters.date_demande} onChange={(v) => update('date_demande', v)} label="date demande" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Employe
                    <ColumnFilter
                      type="autocomplete"
                      value={filters.employee_id}
                      onChange={(v) => update('employee_id', v)}
                      label="employé"
                      color={HR_COLOR}
                      options={employees.map(e => ({ value: e.id.toString(), label: `${e.matricule || ''} ${e.nom_complet}`.trim() }))}
                    />
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    Montant
                    <ColumnFilter type="numberRange" value={filters.montant} onChange={(v) => update('montant', v)} label="montant" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date Paiement
                    <ColumnFilter type="dateRange" value={filters.date_paiement} onChange={(v) => update('date_paiement', v)} label="date paiement" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Statut
                    <ColumnFilter
                      type="select"
                      value={filters.statut}
                      onChange={(v) => update('statut', v)}
                      label="statut"
                      color={HR_COLOR}
                      options={STATUT_ACOMPTE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                    />
                  </Box>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Chargement...</TableCell>
                </TableRow>
              ) : acomptes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Aucune demande trouvee</TableCell>
                </TableRow>
              ) : (
                acomptes.map((acompte) => (
                  <TableRow key={acompte.id} hover>
                    <TableCell>
                      {acompte.ref_acompte ? (
                        <Chip
                          label={acompte.ref_acompte}
                          size="small"
                          sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(acompte.date_demande)}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {acompte.employee?.nom_complet || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {acompte.employee?.matricule}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{formatMoney(acompte.montant)}</TableCell>
                    <TableCell>{formatDate(acompte.date_paiement)}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatutAcompteLabel(acompte.statut)}
                        size="small"
                        color={getStatutAcompteColor(acompte.statut)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {acompte.statut !== 'PAYEE' && (
                          <Tooltip title="Valider/Refuser">
                            <IconButton
                              size="small"
                              onClick={() => handleValidateClick(acompte)}
                              sx={{ color: '#66BB6A' }}
                            >
                              <ValidateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {acompte.statut !== 'PAYEE' && (
                          <Tooltip title="Modifier">
                            <IconButton
                              size="small"
                              onClick={() => handleEditAcompte(acompte)}
                              sx={{ color: HR_COLOR }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {acompte.statut === 'VALIDEE' && (
                          <Tooltip title="Telecharger PDF">
                            <IconButton
                              size="small"
                              onClick={() => handleGeneratePDF(acompte)}
                              sx={{ color: '#1976D2' }}
                            >
                              <PdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {acompte.statut === 'PAYEE' && (
                          <Tooltip title="Telecharger PDF">
                            <IconButton
                              size="small"
                              onClick={() => handleGeneratePDF(acompte)}
                              sx={{ color: '#1976D2' }}
                            >
                              <PdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {acompte.statut !== 'PAYEE' && (
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(acompte)}
                              sx={{ color: '#EF5350' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Lignes par page"
        />
      </Paper>

      {/* Form Dialog */}
      <AcompteFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setFormMinimized(false);
        }}
        acompte={selectedAcompte}
        employees={employees}
        onSuccess={handleSuccess}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      {/* Validation Dialog */}
      <AcompteValidationDialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        acompte={acompteToValidate}
        onSuccess={handleSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        type="warning"
        title="Confirmer la suppression"
        message={`Etes-vous sur de vouloir supprimer la demande d'acompte de ${acompteToDelete?.employee?.nom_complet || 'cet employe'} ?`}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        showCancel={true}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default AcomptesManagement;
